/**
 * Custom Design - styleable SLOTS catalog.
 *
 * A "slot" is a TYPE/ROLE of UI element (not a single DOM node). Editing a slot
 * restyles every matching element across all pages. The element picker maps a
 * clicked DOM node to a slot via `el.closest(selector)`.
 *
 * Ordering matters for picking: list the most specific slots FIRST so a click
 * on a primary button resolves to `primary_button`, not the surrounding `card`.
 *
 * Each slot:
 *   id        - stable key stored in tokens.slots[id]
 *   labelKey  - i18n key (appearance.custom.slots.<id>)
 *   icon      - mdi icon for the panel
 *   category  - 'navigation' | 'actions' | 'surfaces' | 'data' | 'inputs'
 *   match     - selector used by the picker (el.closest)
 *   target    - selector(s) the generated CSS applies to (defaults to match)
 *   props     - which editors to show: subset of ['fill','text']
 *   textTarget- optional override selector for the text color rule
 */
export const SLOTS = [
  {
    id: 'primary_button',
    labelKey: 'appearance.custom.slots.primary_button',
    icon: 'mdi-gesture-tap-button',
    category: 'actions',
    // Match primary-colored buttons across ALL variants (flat/elevated use
    // bg-primary; tonal/outlined/text use text-primary). Exclude bare icon
    // buttons so toolbar icons aren't treated as primary action buttons.
    match: '.v-btn.bg-primary, .v-btn.text-primary:not(.v-btn--icon)',
    target: '.v-btn.bg-primary, .v-btn.text-primary:not(.v-btn--icon)',
    props: ['fill', 'text'],
  },
  {
    id: 'accent_button',
    labelKey: 'appearance.custom.slots.accent_button',
    icon: 'mdi-gesture-tap-button',
    category: 'actions',
    match: '.v-btn.bg-accent, .v-btn.text-accent:not(.v-btn--icon)',
    target: '.v-btn.bg-accent, .v-btn.text-accent:not(.v-btn--icon)',
    props: ['fill', 'text'],
  },
  {
    id: 'chip',
    labelKey: 'appearance.custom.slots.chip',
    icon: 'mdi-label',
    category: 'data',
    match: '.v-chip',
    target: '.v-chip',
    props: ['fill', 'text'],
  },
  {
    id: 'tab',
    labelKey: 'appearance.custom.slots.tab',
    icon: 'mdi-tab',
    category: 'navigation',
    match: '.v-tab, .v-tab__slider',
    target: '.v-tab',
    props: ['text'],
  },
  {
    id: 'input',
    labelKey: 'appearance.custom.slots.input',
    icon: 'mdi-form-textbox',
    category: 'inputs',
    match: '.v-field',
    target: '.v-field',
    props: ['fill', 'text'],
  },
  {
    id: 'table_header',
    labelKey: 'appearance.custom.slots.table_header',
    icon: 'mdi-table-headers-eye',
    category: 'data',
    match: '.v-data-table__th, thead th',
    target: '.v-data-table__th, .v-table thead th',
    props: ['fill', 'text'],
  },
  {
    id: 'dialog',
    labelKey: 'appearance.custom.slots.dialog',
    icon: 'mdi-card-text-outline',
    category: 'surfaces',
    match: '.v-overlay__content > .v-card, .v-dialog .v-card',
    target: '.v-overlay__content > .v-card, .v-dialog .v-card',
    props: ['fill', 'text'],
  },
  {
    id: 'header',
    labelKey: 'appearance.custom.slots.header',
    icon: 'mdi-page-layout-header',
    category: 'navigation',
    match: '.v-app-bar, .dispatch-header',
    target: '.v-app-bar, .dispatch-header',
    props: ['fill', 'text'],
  },
  {
    id: 'sidebar',
    labelKey: 'appearance.custom.slots.sidebar',
    icon: 'mdi-page-layout-sidebar-left',
    category: 'navigation',
    match: '.v-navigation-drawer',
    target: '.v-navigation-drawer',
    props: ['fill', 'text'],
  },
  {
    id: 'card',
    labelKey: 'appearance.custom.slots.card',
    icon: 'mdi-card-outline',
    category: 'surfaces',
    // Plain cards only (exclude dialog cards handled above). Picker order makes
    // dialog/button slots win first, so this is the generic fallback surface.
    match: '.v-card',
    target: '.v-main .v-card',
    props: ['fill', 'text'],
  },
]

export const SLOT_BY_ID = SLOTS.reduce((m, s) => { m[s.id] = s; return m }, {})

/** Map a clicked DOM element to the most specific matching slot id (or null). */
export function slotForElement(el) {
  if (!el) return null
  for (const slot of SLOTS) {
    try {
      if (el.closest(slot.match)) return slot.id
    } catch { /* invalid selector guard */ }
  }
  return null
}

/** Base color tokens (edited as swatches, not via picker). */
export const BASE_COLORS = [
  { id: 'primary', labelKey: 'appearance.custom.base.primary' },
  { id: 'accent', labelKey: 'appearance.custom.base.accent' },
  { id: 'background', labelKey: 'appearance.custom.base.background' },
  { id: 'surface', labelKey: 'appearance.custom.base.surface' },
  { id: 'text_primary', labelKey: 'appearance.custom.base.text_primary' },
  { id: 'text_secondary', labelKey: 'appearance.custom.base.text_secondary' },
  { id: 'success', labelKey: 'appearance.custom.base.success' },
  { id: 'warning', labelKey: 'appearance.custom.base.warning' },
  { id: 'error', labelKey: 'appearance.custom.base.error' },
  { id: 'info', labelKey: 'appearance.custom.base.info' },
]
